import { addDays } from "./date";
import { dayKcal, daySteps } from "./energy";
import { sessionPct } from "./queue";
import { sessionKeyFor } from "./plan";
import type { KayaState, StepSample } from "./types";
import { activeMinutes } from "./training";

/** The three rings. Each is a value against a goal, clamped for display but not for the label. */
export interface Rings {
  steps: { value: number; goal: number };
  kcal: { value: number; goal: number };
  move: { value: number; goal: number };
}

export const DEFAULT_STEP_GOAL = 8000;
export const DEFAULT_KCAL_GOAL = 400;
export const DEFAULT_MOVE_GOAL = 30;

/**
 * Minutes of deliberate movement: the guided session, the treadmill, and any free walks.
 *
 * Not the same as "active minutes" on a watch, which infers effort from heart rate. Kaya only
 * knows what it was told to do and what was logged, so this counts logged work rather than
 * pretending to detect it.
 */
export function moveMinutes(S: KayaState, ds: string): number {
  // One source for "minutes actually exercising" (ADR-033): sessions, extras, walks and the
  // treadmill's own minutes. This used to price a treadmill run from its distance at 5 km/h,
  // which disagreed with the two other active-time figures the app computed.
  return activeMinutes(S, ds);
}

export function rings(S: KayaState, ds: string): Rings {
  const p = S.profile;
  return {
    steps: { value: daySteps(S, ds), goal: p.stepGoal || DEFAULT_STEP_GOAL },
    kcal: { value: dayKcal(S, ds), goal: p.kcalGoal || DEFAULT_KCAL_GOAL },
    move: { value: moveMinutes(S, ds), goal: p.moveMinGoal || DEFAULT_MOVE_GOAL },
  };
}

/**
 * Steps per hour of the day, from the cumulative readings.
 *
 * Each bucket is the difference between the last reading in it and the last one before it, so a
 * gap in sampling lands the steps in the hour they were *noticed*, not spread across hours they
 * may not belong to. Inventing a distribution would look smoother and be wrong.
 */
export function hourlySteps(samples: StepSample[]): number[] {
  const hours = new Array(24).fill(0);
  if (!samples.length) return hours;

  const sorted = [...samples].sort((a, b) => a.t - b.t);
  let prev = sorted[0].steps;
  for (const s of sorted.slice(1)) {
    const h = new Date(s.t).getHours();
    const delta = s.steps - prev;
    if (delta > 0) hours[h] += delta;
    prev = s.steps;
  }
  return hours;
}

/** The last `n` days ending today, oldest first, for the week comparison. */
export function recentSteps(
  S: KayaState,
  today: string,
  n = 7
): { date: string; steps: number }[] {
  const out: { date: string; steps: number }[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const ds = addDays(today, -i);
    out.push({ date: ds, steps: daySteps(S, ds) });
  }
  return out;
}

export function average(rows: { steps: number }[]): number {
  const counted = rows.filter((r) => r.steps > 0);
  if (!counted.length) return 0;
  return Math.round(counted.reduce((a, r) => a + r.steps, 0) / counted.length);
}
